import { deleteCookie, postCookie } from '@app/packages/internalApi/actions/cookie'
import type { ApiDispatcher } from '@app/packages/internalApi/ApiDispatcher'

import type { CookieStore, CookieStoreSetOptions } from './CookieStore'
import { parseCookieHeader } from './parseCookieHeader'

/**
 * Browser-side {@link CookieStore}. Reads parse `document.cookie`; writes go
 * exclusively through the internal `/cookie` endpoint (the server owns the
 * `Set-Cookie`, which lets it write httpOnly cookies the page can't). The
 * browser cookie jar therefore updates only once the request response is applied
 * — non-httpOnly writes are eventually consistent. Writes are best-effort.
 */
export class BrowserCookieStore implements CookieStore {
  constructor(private readonly deps: { apiDispatcher: ApiDispatcher }) {}

  get(name: string): Promise<string | undefined> {
    return Promise.resolve(parseCookieHeader(document.cookie).get(name))
  }

  async set(name: string, value: string, options?: CookieStoreSetOptions): Promise<void> {
    await this.deps.apiDispatcher
      .dispatch(
        postCookie({
          name,
          value,
          options: {
            path: options?.path ?? '/',
            httpOnly: options?.httpOnly ?? false,
            sameSite: options?.sameSite ?? 'Lax',
            expires: options?.expires?.toISOString(),
            domain: options?.domain,
            maxAge: options?.maxAge,
            overwrite: true,
          },
        })
      )
      .catch(() => {})
  }

  async remove(name: string, options?: CookieStoreSetOptions): Promise<void> {
    await this.deps.apiDispatcher.dispatch(deleteCookie({ name, options: { path: options?.path ?? '/', domain: options?.domain } })).catch(() => {})
  }
}
